import { TemplateFormat } from '@langchain/core/prompts';
import { type Node } from '@tiptap/pm/model';
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Extension } from '@tiptap/react';

import { VarMappingPropsWithValidation } from '@/components/EvaluatorCrudPane/types';

import {
  Segment,
  getDocIndexFromOffset,
  getNodesFromTemplate,
} from './RichTextEditor.utils';
import {
  createVariableMappingDropdown,
  unmountVariableMappingDropdowns,
} from './renderVariableMapping';

/**
 * Checks if two arrays have different contents
 * @returns true if arrays have changed, false if they're the same
 */
function hasArrayChanged<T>(
  oldArray: T[] | null | undefined,
  newArray: T[] | null | undefined
): boolean {
  if (oldArray?.length !== newArray?.length) return true;

  return !oldArray?.every((item, i) => newArray?.[i] === item) || false;
}

// Utility function for comparing mappings
function hasVariableMappingsChanged(
  oldMappings: Record<string, string> | null | undefined,
  newMappings: Record<string, string> | null | undefined
): boolean {
  const oldKeys = Object.keys(oldMappings || {});
  const newKeys = Object.keys(newMappings || {});

  if (oldKeys.length !== newKeys.length) return true;

  return oldKeys.some((key) => oldMappings?.[key] !== newMappings?.[key]);
}
export const variableHighlighterPluginKey = new PluginKey(
  'variableHighlighter'
);
// Extension with commands using the utility functions
export const variableHighlighterExtension = ({
  templateFormat,
  acceptedVariableList,
  readOnly,
}: {
  templateFormat: TemplateFormat;
  acceptedVariableList?: string[] | null;
  readOnly?: boolean;
}) =>
  Extension.create({
    name: 'variableHighligher',
    addStorage() {
      return {
        variableMappings: null,
        availablePaths: null,
        uneditableVariables: [],
        storeType: 'crud',
        isSuggestions: false,
      };
    },
    addCommands() {
      return {
        updateVariableMappings:
          (
            variableMappings: Record<string, string> | null,
            storeType: VarMappingPropsWithValidation['storeType']
          ) =>
          () => {
            this.storage.storeType = storeType;
            if (
              hasVariableMappingsChanged(
                this.storage.variableMappings,
                variableMappings
              )
            ) {
              this.storage.variableMappings = variableMappings;

              // Dispatch update directly within the command
              const tr = this.editor.view.state.tr;
              tr.setMeta('variableHighligher', {
                variableMappings,
                storeType,
              });
              this.editor.view.dispatch(tr);
            }
            return true;
          },

        updateAvailablePaths: (availablePaths: string[] | null) => () => {
          if (hasArrayChanged(this.storage.availablePaths, availablePaths)) {
            this.storage.availablePaths = availablePaths;

            // Dispatch update
            const tr = this.editor.view.state.tr;
            tr.setMeta('variableHighligher', {
              availablePaths,
            });
            this.editor.view.dispatch(tr);
          }
          return true;
        },

        updateUneditableVariables: (uneditableVariables: string[]) => () => {
          if (
            hasArrayChanged(
              this.storage.uneditableVariables,
              uneditableVariables
            )
          ) {
            this.storage.uneditableVariables = uneditableVariables;

            // Dispatch update
            const tr = this.editor.view.state.tr;
            tr.setMeta('variableHighligher', {
              uneditableVariables,
            });
            this.editor.view.dispatch(tr);
          }
          return true;
        },

        updateShowValidationErrors: (showValidationErrors: boolean) => () => {
          this.storage.showValidationErrors = showValidationErrors;
          // Dispatch update
          const tr = this.editor.view.state.tr;
          tr.setMeta('variableHighligher', {
            showValidationErrors,
          });
          this.editor.view.dispatch(tr);
          return true;
        },

        updateIsSuggestions: (isSuggestions: boolean) => () => {
          this.storage.isSuggestions = isSuggestions;
          // Dispatch update
          const tr = this.editor.view.state.tr;
          tr.setMeta('variableHighligher', {
            isSuggestions,
          });
          this.editor.view.dispatch(tr);
          return true;
        },

        registerUpdateCallbacks:
          ({ setVariableMappings, setActiveVariable }) =>
          () => {
            this.storage.setVariableMappings = (
              variableMappings: Record<string, string>
            ) => {
              const tr = this.editor.view.state.tr;
              tr.setMeta('variableHighligher', {
                variableMappings,
              });
              this.editor.view.dispatch(tr);
              setVariableMappings(variableMappings);
            };
            this.storage.setActiveVariable = setActiveVariable;
            this.storage.removeVariable = (start, end) => {
              const tr = this.editor.view.state.tr;
              tr.delete(start, end);
              this.editor.view.dispatch(tr);
            };

            // Allows renaming a variable by replacing text in the document and
            // updating the variableMappings object.
            this.storage.renameVariable = (
              start: number,
              end: number,
              oldKey: string,
              newKey: string,
              variableMappings: Record<string, string>
            ): [boolean, string] => {
              if (!newKey || newKey === oldKey) return [true, ''];

              // In suggestions mode, allow duplicate variables by using unique mapping keys
              const isSuggestions = this.storage.isSuggestions;
              if (isSuggestions) {
                const tr = this.editor.view.state.tr;

                // Read the current text slice (including delimiters) so we can
                // substitute just the variable name while preserving any
                // section markers (e.g. #, ^, /) or unescaped markers (&, >).
                const original = this.editor.view.state.doc.textBetween(
                  start,
                  end,
                  undefined,
                  '\u0000'
                );

                const replacement = original.replace(oldKey, newKey);

                tr.insertText(replacement, start, end);
                this.editor.view.dispatch(tr);

                // Update variable mappings if present
                if (this.storage.variableMappings?.[oldKey]) {
                  const updatedMappings = {
                    ...this.storage.variableMappings,
                    [newKey]: newKey,
                  };

                  // Re-use existing helper to propagate updates
                  this.storage.setVariableMappings(updatedMappings);
                }
                return [true, ''];
              } else {
                if (newKey in variableMappings)
                  return [false, 'Variable already exists'];

                const tr = this.editor.view.state.tr;

                // Read the current text slice (including delimiters) so we can
                // substitute just the variable name while preserving any
                // section markers (e.g. #, ^, /) or unescaped markers (&, >).
                const original = this.editor.view.state.doc.textBetween(
                  start,
                  end,
                  undefined,
                  '\u0000'
                );

                const replacement = original.replace(oldKey, newKey);

                tr.insertText(replacement, start, end);
                this.editor.view.dispatch(tr);

                // Update variable mappings if present
                if (this.storage.variableMappings?.[oldKey]) {
                  const updatedMappings = {
                    ...this.storage.variableMappings,
                    [newKey]: this.storage.variableMappings[oldKey],
                  };

                  delete updatedMappings[oldKey];

                  // Re-use existing helper to propagate updates
                  this.storage.setVariableMappings(updatedMappings);
                }
                return [true, ''];
              }
            };
            return true;
          },
      };
    },

    addProseMirrorPlugins() {
      // Helper function to get decoration class
      function getDecorationClass(
        segment: { text: string },
        acceptedVariableList: string[] | null | undefined,
        shouldInsertWidget: boolean
      ): string {
        return acceptedVariableList
          ? acceptedVariableList.includes(segment.text)
            ? 'text-brand-green-400'
            : 'text-error'
          : shouldInsertWidget
          ? 'invisible w-[1px] h-[13px] overflow-hidden inline-block'
          : 'text-brand-green-400';
      }

      // Helper function to process variable segments
      function processVariableSegment(
        segment: Segment,
        inputBounds: number[],
        variableMappings: Record<string, string> | null,
        storage: any,
        availablePaths: string[],
        storeType: 'crud' | 'detail' | 'alignPlayground',
        uneditableVariables: string[],
        showValidationErrors: boolean,
        readOnly: boolean | undefined,
        acceptedVariableList: string[] | null | undefined,
        isSuggestions?: boolean
      ): Decoration[] {
        const decorations: Decoration[] = [];
        const start = getDocIndexFromOffset(inputBounds, segment.cursor);
        const end = getDocIndexFromOffset(
          inputBounds,
          segment.cursor + segment.textWithDelimiters.length
        );
        const shouldInsertWidget =
          variableMappings && !segment.text.includes('{{');

        if (shouldInsertWidget) {
          decorations.push(
            Decoration.widget(
              start,
              createVariableMappingDropdown({
                evaluatorContext: storeType,
                variableMappingKey: segment.text,
                variableMappingValue: variableMappings[segment.text],
                availablePaths,
                onSelect: (key, path) => {
                  storage.setVariableMappings({
                    ...variableMappings,
                    [key]: path,
                  });
                },
                setActiveVariable: storage.setActiveVariable ?? (() => {}),
                onRename: (oldKey, newKey) => {
                  return (
                    storage.renameVariable?.(
                      start,
                      end,
                      oldKey,
                      newKey,
                      variableMappings
                    ) ?? [true, '']
                  );
                },
                onRemove: () => storage.removeVariable(start, end),
                uneditableVariables,
                showValidationErrors: showValidationErrors ?? false,
                readOnly,
                isSuggestions,
                position: start,
              })
            )
          );
        }

        decorations.push(
          Decoration.inline(start, end, {
            class: getDecorationClass(
              segment,
              acceptedVariableList,
              !!shouldInsertWidget
            ),
          })
        );

        return decorations;
      }

      // Helper function to process nested segments
      function processNestedSegment(
        segment: Segment,
        inputBounds: number[],
        variableMappings: Record<string, string> | null,
        storage,
        availablePaths: string[],
        storeType: 'crud' | 'detail' | 'alignPlayground',
        uneditableVariables: string[],
        showValidationErrors: boolean,
        readOnly: boolean | undefined,
        acceptedVariableList: string[] | null | undefined,
        isSuggestions?: boolean
      ): Decoration[] {
        const decorations: Decoration[] = [];
        const openingTagMatch =
          segment.textWithDelimiters.match(/^(\{\{#[^}]+\}\})/);

        if (!openingTagMatch) return decorations;

        const openingTagLength = openingTagMatch[1].length;
        const openingTagStart = getDocIndexFromOffset(
          inputBounds,
          segment.cursor
        );
        const openingTagEnd = getDocIndexFromOffset(
          inputBounds,
          segment.cursor + openingTagLength
        );
        const contentStart = openingTagEnd;
        const contentEnd = getDocIndexFromOffset(
          inputBounds,
          segment.cursor + segment.textWithDelimiters.length
        );
        const shouldInsertWidget =
          variableMappings && !segment.text.includes('{{');

        // Create widget for opening tag
        if (shouldInsertWidget) {
          decorations.push(
            Decoration.widget(
              openingTagStart,
              createVariableMappingDropdown({
                evaluatorContext: storeType,
                variableMappingKey: segment.text,
                variableMappingValue: variableMappings[segment.text],
                availablePaths,
                onSelect: (key, path) => {
                  storage.setVariableMappings({
                    ...variableMappings,
                    [key]: path,
                  });
                },
                setActiveVariable: storage.setActiveVariable ?? (() => {}),
                onRename: (oldKey, newKey) =>
                  storage.renameVariable?.(
                    openingTagStart,
                    openingTagEnd,
                    oldKey,
                    newKey,
                    variableMappings
                  ) ?? [true, ''],
                onRemove: () =>
                  storage.removeVariable(openingTagStart, contentEnd),
                uneditableVariables,
                showValidationErrors: showValidationErrors ?? false,
                readOnly,
                isSuggestions,
                position: openingTagStart,
              })
            )
          );
        }

        // Style opening tag
        decorations.push(
          Decoration.inline(openingTagStart, openingTagEnd, {
            class: getDecorationClass(
              segment,
              acceptedVariableList,
              !!shouldInsertWidget
            ),
          })
        );

        // Hide remaining content
        if (contentStart < contentEnd) {
          decorations.push(
            Decoration.inline(contentStart, contentEnd, {
              class: 'hidden',
            })
          );
        }

        return decorations;
      }

      function getTokenDecorations(params: {
        doc: Node;
        transaction?: Transaction;
        templateFormat: TemplateFormat;
        acceptedVariableList?: string[] | null;
        storage: Record<string, any>;
        oldDecorationSet?: DecorationSet;
      }): DecorationSet {
        const {
          doc,
          transaction,
          templateFormat,
          acceptedVariableList,
          storage,
        } = params;

        let inputText = '';
        const inputBounds: number[] = [];

        /**
         * rich:        |   | a | a | ... | a  |    | \n |    | \n |    | \n | b  | b  | b  | b  | b  |    |
         * plain index  |   | 0 | 1 | ... | 14 | .. | 15 | .. | 16 | .. | 17 | 18 | 19 | 20 | 21 | 22 | .. |
         * offsets      | +1                        | +2      | +3      | +4                               |
         * rich pars    | p0                        | p1      | p2      | p3                               |
         * rich index   | 0 | 1 | 2 | ... | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24 | 25 | 26 | 27 |
         */
        doc.descendants((node, position) => {
          const insertText = (position > 0 ? `\n` : '') + node.textContent;
          inputBounds.push(inputText.length);
          inputText += insertText;
          return false;
        });

        const newDecorations: Decoration[] = [];

        const transactionMeta = transaction?.getMeta('variableHighligher');
        const availablePaths: string[] | null =
          transactionMeta?.availablePaths ?? storage.availablePaths;
        const variableMappings: Record<string, string> | null =
          transactionMeta?.variableMappings ?? storage.variableMappings;
        const storeType: 'crud' | 'detail' | 'alignPlayground' =
          transactionMeta?.storeType ?? storage.storeType;
        const uneditableVariables: string[] | null =
          transactionMeta?.uneditableVariables ?? storage.uneditableVariables;
        const isSuggestions: boolean =
          transactionMeta?.isSuggestions ?? storage.isSuggestions;

        try {
          const segments = getNodesFromTemplate(
            inputText,
            templateFormat,
            true,
            !!variableMappings
          );

          for (const segment of segments) {
            if (segment.type === 'variable' || segment.type === 'nested') {
              const processFunction =
                segment.type === 'variable'
                  ? processVariableSegment
                  : processNestedSegment;

              newDecorations.push(
                ...processFunction(
                  segment,
                  inputBounds,
                  variableMappings,
                  storage,
                  availablePaths ?? [],
                  storeType,
                  uneditableVariables ?? [],
                  transactionMeta?.showValidationErrors ??
                    storage.showValidationErrors ??
                    false,
                  readOnly,
                  acceptedVariableList,
                  isSuggestions
                )
              );
            }
          }
        } catch {
          // pass
        }

        return DecorationSet.create(doc, newDecorations);
      }

      return [
        new Plugin({
          key: variableHighlighterPluginKey,
          state: {
            init: (_, { doc }) =>
              getTokenDecorations({
                doc,
                templateFormat,
                acceptedVariableList,
                storage: this.editor.storage['variableHighligher'],
              }),
            apply: (transaction, oldDecorationSet) => {
              const isVariableHighlighterChange =
                transaction.getMeta('variableHighligher');
              const storage = this.editor.storage['variableHighligher'];

              if (transaction.docChanged || isVariableHighlighterChange) {
                return getTokenDecorations({
                  doc: transaction.doc,
                  transaction,
                  templateFormat,
                  acceptedVariableList,
                  storage,
                  oldDecorationSet,
                });
              }
              return oldDecorationSet;
            },
          },
          props: {
            decorations(state) {
              return this.getState(state);
            },
          },
        }),
      ];
    },

    /**
     * Ensure we properly clean up any cached React roots that were mounted via
     * `createVariableMappingDropdown` when the editor instance is destroyed.
     */
    onDestroy() {
      unmountVariableMappingDropdowns(this.storage.storeType);
    },
  });
